import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getDashboardStats } from "../../../services/operations/dashboardAPI";

const SuperAdmin = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getDashboardStats();
        //console.log("res",response)

        if (response) {
          // Handle apiCallsToday whether it's a number or object
          const apiCallsFormatted =
            typeof response.apiCallsToday === "object"
              ? `${response.apiCallsToday.used || 0} / ${response.apiCallsToday.limit || 0}`
              : `${response.apiCallsToday || 0} / 0`;

          setStats([
            {
              label: "Total Schools",
              value: response.totalSchools || 0,
              color: "bg-blue-100 text-blue-800",
            },
            {
              label: "Total Users",
              value: response.totalUsers || 0,
              color: "bg-teal-100 text-teal-800",
            },
            {
              label: "Total Students",
              value: response.totalStudents || 0,
              color: "bg-indigo-100 text-indigo-800",
            },
            {
              label: "Total Teachers",
              value: response.totalTeachers || 0,
              color: "bg-purple-100 text-purple-800",
            },
            {
              label: "Total Parents",
              value: response.totalParents || 0,
              color: "bg-gray-100 text-gray-800",
            },
            {
              label: "Active Subscriptions",
              value: response.activeSubscriptions || 0,
              color: "bg-green-100 text-green-800",
            },
            {
              label: "Expired Plans",
              value: response.expiredPlans || 0,
              color: "bg-red-100 text-red-800",
            },
            {
              label: "API Usage Today",
              value: `${response.apiCallsToday || 0} / ${response.totalApiLimitPerDay || 0}`,
              color: "bg-indigo-50 text-indigo-800",
            },
          ]);
        }
      } catch (err) {
        console.error("Dashboard Stats Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* TITLE */}
      <motion.h1
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-bold mb-6 text-gray-800"
      >
        Super Admin Dashboard
      </motion.h1>

      {/* Loading State */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="h-28 bg-gray-200 animate-pulse rounded-2xl"
            ></div>
          ))}
        </div>
      )}

      {/* ANALYTICS CARDS */}
      {!loading && stats.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          {stats.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className={`p-5 rounded-2xl shadow-md border ${item.color}`}
            >
              <p className="text-lg">{item.label}</p>
              <h2 className="text-3xl font-semibold mt-1">{item.value}</h2>
            </motion.div>
          ))}
        </div>
      )}

      {/* MAIN NAV GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        <DashboardCard
          title="School Management"
          desc="Add, edit, delete and manage schools."
          link="/dashboard/super-admin/schools"
          color="bg-white border-blue-500 hover:bg-blue-50 text-blue-700"
        />
        <DashboardCard
          title="Subscription Management"
          desc="Activate, renew or expire school subscriptions."
          link="/dashboard/super-admin/subscriptions"
          color="bg-white border-green-500 hover:bg-green-50 text-green-700"
        />
        <DashboardCard
          title="User Management"
          desc="View all users: students, teachers, parents, admins."
          link="/dashboard/super-admin/users"
          color="bg-white border-indigo-500 hover:bg-indigo-50 text-indigo-700"
        />
        <DashboardCard
          title="Curriculum Management"
          desc="Build and update the academic curriculum across all classes."
          link="/dashboard/super-admin/subject-manager"
          color="bg-white border-emerald-500 hover:bg-emerald-50 text-emerald-700"
        />
        <DashboardCard
          title="Curriculum Request"
          desc="Review, approve, and manage curriculum requests for all classes and subjects."
          link="/dashboard/super-admin/subject-request"
          color="bg-white border-indigo-500 hover:bg-indigo-50 text-indigo-700"
        />
      </div>
    </div>
  );
};

const DashboardCard = ({ title, desc, link, color }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className={`p-6 cursor-pointer rounded-2xl shadow-md border-l-4 transition-all ${color}`}
  >
    <Link to={link}>
      <h3 className="text-xl font-semibold">{title}</h3>
      <p className="mt-2 opacity-90">{desc}</p>
    </Link>
  </motion.div>
);

export default SuperAdmin;
